// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { delay } from 'lodash';

// Utils
import { getFetching } from '../attachments/attachmentsSelector';
import { prop } from '../../../common/utils/immutableHelper';
import { getElementId, getLinkType } from '../../../common/elements/utils/elementPropertyUtils';

// Constants
import { KEY_CODES } from '../../utils/keyboard/keyConstants';
import { LINK_ELEMENT_LINK_TYPES, LINK_TYPE } from '../../../common/links/linkConstants';

// Styles
import './LinkInput.scss';

const getLinkInputPlaceholder = (element) => {
    switch (getLinkType(element)) {
        case LINK_ELEMENT_LINK_TYPES.VIDEO:
            return 'Paste a link from YouTube or Vimeo';
        case LINK_ELEMENT_LINK_TYPES.AUDIO:
            return 'Paste a link from Spotify or Soundcloud';
        case LINK_ELEMENT_LINK_TYPES.MAP:
            return 'Paste a link from Google Maps';
        default:
            return 'Enter a link URL';
    }
};

class LinkInput extends React.Component {
    constructor(props) {
        super(props);

        this.linkInputRef = React.createRef();
        this.linkContainerRef = React.createRef();
    }

    componentDidMount() {
        this.mounted = true;
    }

    componentWillReceiveProps(nextProps) {
        const { setTempContent } = this.props;

        // handle error
        const url = this.props.element.getIn(['content', 'temp', 'url']);
        const error = this.props.element.getIn(['content', 'temp', 'error']);

        const nextError = nextProps.element.getIn(['content', 'temp', 'error']);

        if (!error && nextError) {
            this.linkContainerRef.current && this.linkContainerRef.current.focus();

            this.errorTime = setTimeout(() => {
                setTempContent({ error: null, url });
                this.errorTime = null;
            }, 2000);
        }
    }

    componentDidUpdate(oldProps) {
        // focus on mount
        const error = this.props.element.getIn(['content', 'temp', 'error']);
        const oldError = oldProps.element.getIn(['content', 'temp', 'error']);

        if ((this.props.isEditing && !oldProps.isEditing) || (this.props.isSelected && !oldProps.isSelected)) {
            delay(() => {
                // If already unmounted don't attempt focus
                if (!this.mounted) return;

                if (!this.props.shouldFocusOnlyWhenSelected && this.props.isSelected) this.focus();

                // focus after its already selected
                if (this.props.shouldFocusOnlyWhenSelected && oldProps.isSelected) this.focus();
            }, 10);
        }

        if ((!this.props.isEditing && oldProps.isEditing) || (!this.props.isSelected && oldProps.isSelected)) {
            this.linkInputRef.current && this.linkInputRef.current.blur();

            const url = this.props.element.getIn(['content', 'temp', 'url']);
            if (url) this.handleSubmit();
        }

        if (!error && oldError && this.props.isSelected) {
            this.focus();
        }
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    startEditingElement = () => {
        const { startEditing, element } = this.props;

        startEditing &&
            startEditing({
                editorId: `${getElementId(element)}-${LINK_TYPE}`,
                editorKey: LINK_TYPE,
            });
    };

    onClick = () => {
        if (
            !this.props.shouldFocusOnlyWhenSelected ||
            (this.props.shouldFocusOnlyWhenSelected && this.props.isSelected)
        )
            this.startEditingElement();
    };

    focus = () => {
        this.linkInputRef.current && this.linkInputRef.current.focus();
    };

    handleMouseDown = (event) => {
        const { isEditing } = this.props;

        if (isEditing) {
            event.stopPropagation();
        }
    };

    handleSubmit = (event) => {
        event && event.preventDefault();

        const { setLinkUrl, setTempContent, element, deselectAll } = this.props;

        const url = element.getIn(['content', 'temp', 'url']);

        const validationResponse = setLinkUrl(url);

        if (validationResponse) {
            setTempContent({ error: validationResponse, url });
        }

        deselectAll && deselectAll();
    };

    handleChange = (event) => {
        const { setTempContent } = this.props;
        setTempContent({ url: event.target.value });
    };

    handleKey = (event) => {
        event.stopPropagation();
        const { element, setTempContent, stopEditing, moveElementsToTrash } = this.props;

        if (event.which === KEY_CODES.ESC) {
            return stopEditing();
        }

        const url = element.getIn(['content', 'temp', 'url']);
        const error = element.getIn(['content', 'temp', 'error']);

        if (!url && (event.which === KEY_CODES.BACKSPACE || event.which === KEY_CODES.DELETE)) {
            event.preventDefault();
            moveElementsToTrash && moveElementsToTrash();
        }

        if (error) {
            if (this.errorTime) {
                clearTimeout(this.errorTime);
                this.errorTime = null;
            }

            return setTempContent({ error: null, url });
        }
    };

    render() {
        const { isEditing, element, attachment } = this.props;
        const url = element.getIn(['content', 'temp', 'url']);
        const error = element.getIn(['content', 'temp', 'error']);
        const fetching = getFetching(attachment);

        const inputError = <div className="error-message">{(error && prop('message', error)) || 'URL not found'}</div>;

        const input = (
            <div className="input-wrapper" onClick={this.onClick}>
                <input
                    onChange={this.handleChange}
                    onKeyDown={this.handleKey}
                    value={url || ''}
                    ref={this.linkInputRef}
                    placeholder={getLinkInputPlaceholder(element)}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    enterkeyhint="Done"
                    disabled={!isEditing || fetching}
                />
            </div>
        );

        return (
            <div
                ref={this.linkContainerRef}
                className="LinkInput"
                tabIndex="-1"
                onKeyDown={this.handleKey}
                onMouseDown={this.handleMouseDown}
            >
                <form className="link-form" onSubmit={this.handleSubmit}>
                    {error ? inputError : input}
                </form>
            </div>
        );
    }
}

LinkInput.propTypes = {
    element: PropTypes.object,
    attachment: PropTypes.object,
    isSelected: PropTypes.bool,
    isEditing: PropTypes.bool,
    shouldFocusOnlyWhenSelected: PropTypes.bool,
    setLinkUrl: PropTypes.func,
    setTempContent: PropTypes.func,
    startEditing: PropTypes.func,
    stopEditing: PropTypes.func,
    moveElementsToTrash: PropTypes.func,
    deselectAll: PropTypes.func,
};

export default LinkInput;
